import Link from 'next/link'
import { useRouter } from 'next/router'

import { sectionSchemaRegistry } from '@/lib/section-schema-registry'
import { cn } from '@/lib/utils'
import useLocale from '@/hooks/use-locale'

import { Button } from '../ui/button'

import type { SectionElementSchema } from '@/types/element-schema.type'

interface MoneyBackGuaranteeProps {
  className?: string
  /** Main heading */
  heading?: string
  /** Description text */
  description?: string
  /** Note text (shown for certain locales) */
  noteText?: string
  /** Button text */
  buttonText?: string
  /** Background gradient from color */
  bgGradientFrom?: string
  /** Background gradient to color */
  bgGradientTo?: string
  /** Container max width */
  maxWidth?: string
  /** Min height */
  minHeight?: string
  // Course props (for backward compatibility)
  isCourseEnrolled?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  course?: Record<string, any>
}

// Element schema for Puck editor
export const moneyBackGuaranteeSchema: SectionElementSchema = {
  componentType: 'MoneyBackGuarantee',
  label: 'Money Back Guarantee',
  category: 'content',
  description: 'Display money-back guarantee section with CTA',
  elements: [
    {
      key: 'heading',
      type: 'textarea',
      label: 'Heading',
      default: '100% Money-back Guarantee!',
    },
    {
      key: 'description',
      type: 'textarea',
      label: 'Description',
      default:
        "If you try our sessions and feel it's not the right fit, you're entitled to a 100% refund - no questions asked!",
    },
    {
      key: 'noteText',
      type: 'textarea',
      label: 'Note Text',
      helpText: 'Optional note displayed below description',
      default: '*Note: This guarantee is applicable only to the English version.',
    },
    {
      key: 'buttonText',
      type: 'text',
      label: 'Button Text',
      default: 'Get Started',
    },
    {
      key: 'bgGradientFrom',
      type: 'color',
      label: 'Background Gradient Start',
      default: '#b91c1c', // red-700
    },
    {
      key: 'bgGradientTo',
      type: 'color',
      label: 'Background Gradient End',
      default: '#991b1b', // red-900
    },
    {
      key: 'maxWidth',
      type: 'select',
      label: 'Container Width',
      options: [
        { label: 'Small', value: 'max-w-4xl' },
        { label: 'Medium', value: 'max-w-5xl' },
        { label: 'Large', value: 'max-w-6xl' },
        { label: 'Extra Large', value: 'max-w-7xl' },
      ],
      default: 'max-w-7xl',
    },
    {
      key: 'minHeight',
      type: 'select',
      label: 'Minimum Height',
      options: [
        { label: 'Auto', value: 'auto' },
        { label: 'Small', value: 'min-h-[20vh]' },
        { label: 'Medium', value: 'min-h-[40vh]' },
        { label: 'Large', value: 'min-h-[60vh]' },
      ],
      default: 'min-h-[40vh]',
    },
    {
      key: 'className',
      type: 'text',
      label: 'CSS Class',
    },
  ],
}

// Register schema
sectionSchemaRegistry.register(moneyBackGuaranteeSchema)

export default function MoneyBackGuarantee({
  className,
  heading = '100% Money-back Guarantee!',
  description = "If you try our sessions and feel it's not the right fit, you're entitled to a 100% refund - no questions asked!",
  noteText = '*Note: This guarantee is applicable only to the English version.',
  buttonText,
  bgGradientFrom = '#b91c1c',
  bgGradientTo = '#991b1b',
  maxWidth = 'max-w-7xl',
  minHeight = 'min-h-[40vh]',
  course,
  isCourseEnrolled = false,
}: MoneyBackGuaranteeProps) {
  const { text, locale } = useLocale()
  const router = useRouter()

  const displayButtonText = buttonText || text.MAIN.INTRODUCTION.HEADING_5

  return (
    <div className={cn('flex items-center justify-center my-20 px-4', className)}>
      <div
        className={cn(
          'flex flex-col items-center justify-center p-6 text-center w-full rounded-3xl',
          maxWidth,
          minHeight
        )}
        style={{
          background: `linear-gradient(to bottom right, ${bgGradientFrom}, ${bgGradientTo})`,
        }}
      >
        <div className="space-y-6">
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">{heading}</h1>
          <p className="text-white text-opacity-90 text-base md:text-lg font-semibold mb-6 max-w-xl mx-auto leading-relaxed">
            {description}
          </p>

          {locale === 'en' && noteText && <p className="text-sm italic text-white text-opacity-80">{noteText}</p>}

          <Button size="lg" asChild className="w-full sm:w-auto bg-white text-black hover:bg-gray-100">
            <Link
              href={
                isCourseEnrolled
                  ? '/my-courses'
                  : course
                    ? course.id === 'mlops-1'
                      ? `/checkout?course=${course?.id}`
                      : router.asPath
                    : `/buy-now?course=mlops-1`
              }
              rel="noopener noreferrer"
            >
              {isCourseEnrolled
                ? text.MAIN.INTRODUCTION.HEADING_6
                : course?.id === 'mlops-1' || !course
                  ? displayButtonText
                  : text.MAIN.INTRODUCTION.HEADING_7}{' '}
            </Link>
          </Button>
        </div>
      </div>
    </div>
  )
}
